import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { Club } from '../api/types';

const TIERS = [3, 6, 12];

export function ClubPage() {
  const [club, setClub] = useState<Club | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ user_id: '', niche: '', tier_months: 12 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.club().then(setClub).catch(() => setError('Раздел недоступен.'));
  }, []);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      setClub(
        await api.addResident({
          user_id: Number(form.user_id),
          niche: form.niche.trim(),
          tier_months: form.tier_months,
        }),
      );
      setForm({ user_id: '', niche: '', tier_months: 12 });
    } catch {
      setError('Не получилось. Ниша уже занята, мест нет или человек уже резидент.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(userId: number) {
    setBusy(true);
    try {
      setClub(await api.removeResident(userId));
    } finally {
      setBusy(false);
    }
  }

  if (!club) {
    return (
      <div className="screen">
        <h1 className="screen__title">Клуб</h1>
        {error ? <p className="muted">{error}</p> : <div className="skeleton" />}
      </div>
    );
  }

  const occupied = club.total_seats - club.free_seats;

  return (
    <div className="screen">
      <h1 className="screen__title">Клуб</h1>
      <p className="muted">
        Занято {occupied} из {club.total_seats}. Ниша у резидента одна на весь
        клуб — это гарантирует база, а не проверка при вводе.
      </p>

      {/* Сетка мест: заполненность клуба должна читаться за секунду,
          а не пересчитываться по списку. */}
      <div className="seats">
        {club.seats.map((seat) => (
          <div
            key={seat.seat_number}
            className={`seat${seat.user_id ? ' seat--taken' : ''}`}
            title={seat.full_name ?? 'свободно'}
          >
            {seat.seat_number}
          </div>
        ))}
      </div>

      <section className="block">
        <h2 className="block__title">Резиденты</h2>
        {occupied === 0 && <p className="muted">Пока никого.</p>}
        {club.seats
          .filter((seat) => seat.user_id)
          .map((seat) => (
            <div key={seat.seat_number} className="resident">
              <div>
                <div className="resident__name">
                  {seat.seat_number}. {seat.full_name ?? `id${seat.user_id}`}
                </div>
                <div className="muted small">
                  {seat.niche ?? 'ниша не указана'}
                  {seat.ends_on && ` · до ${seat.ends_on}`}
                </div>
              </div>
              <button
                className="btn btn--secondary btn--slim"
                disabled={busy}
                onClick={() => seat.user_id && remove(seat.user_id)}
              >
                Снять
              </button>
            </div>
          ))}
      </section>

      <section className="block">
        <h2 className="block__title">Добавить резидента</h2>
        <p className="muted">
          Нужен id участника — он виден в списке людей. Место занимается первое
          свободное.
        </p>

        <label className="field">
          <span className="field__label">id участника</span>
          <input
            className="field__input"
            inputMode="numeric"
            value={form.user_id}
            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Ниша</span>
          <input
            className="field__input"
            value={form.niche}
            onChange={(e) => setForm({ ...form, niche: e.target.value })}
          />
        </label>

        <span className="field__label">Срок</span>
        <div className="picks">
          {TIERS.map((months) => (
            <button
              key={months}
              type="button"
              className={`pick${form.tier_months === months ? ' pick--on' : ''}`}
              onClick={() => setForm({ ...form, tier_months: months })}
            >
              {months} мес
            </button>
          ))}
        </div>

        {error && <p className="savebar__error">{error}</p>}

        <button
          className="btn"
          disabled={busy || !form.user_id || form.niche.trim().length < 2}
          onClick={add}
        >
          {busy ? 'Сохраняю…' : 'Добавить'}
        </button>
      </section>
    </div>
  );
}
